// ng-repeat по алгоритму AngularJS 1.2.29: блоки сопоставляются по ключу (track by или
// объект), существующие блоки переиспользуются и переставляются, новые вставляются
// после предыдущего блока, удалённые уходят через анимацию ng-leave.
import { reportError } from './report';

const objectIds = new WeakMap();
let nextObjectId = 1;

function hashKey(value) {
    if (value !== null && (typeof value === 'object' || typeof value === 'function')) {
        if (!objectIds.has(value)) {
            objectIds.set(value, `object:${nextObjectId++}`);
        }
        return objectIds.get(value);
    }
    return `${typeof value}:${value}`;
}

function isArrayLike(value) {
    if (value == null || typeof value === 'string' || typeof value === 'function') {
        return false;
    }
    return Array.isArray(value) || (typeof value.length === 'number' && value.length >= 0 && (value.length === 0 || (value.length - 1) in value));
}

function blockStart(block) {
    return block.clone.__sdReplacedBy || block.clone;
}

function blockNodes(block) {
    const nodes = [];
    for (let node = blockStart(block); node; node = node.nextSibling) {
        nodes.push(node);
        if (node === block.end) {
            break;
        }
    }
    return nodes;
}

export function registerRepeat(directives) {
    directives.set('ngRepeat', {
        priority: 1000,
        terminal: true,
        transclude: 'element',
        link(scope, anchor, attrs, runtime, transclude) {
            const expression = attrs.ngRepeat;
            const match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);
            if (!match) {
                reportError(`ng-repeat: неверное выражение «${expression}»`);
                return;
            }
            const lhs = match[1];
            const rhs = match[2];
            const trackByExp = match[3];
            const ids = lhs.match(/^(?:([$\w]+)|\(([$\w]+)\s*,\s*([$\w]+)\))$/);
            if (!ids) {
                reportError(`ng-repeat: неверная левая часть «${lhs}»`);
                return;
            }
            const valueName = ids[3] || ids[1];
            const keyName = ids[2];
            let trackBy;
            if (trackByExp) {
                const getter = runtime.parse(trackByExp);
                trackBy = (key, value, index) => {
                    const locals = { $index: index };
                    locals[valueName] = value;
                    if (keyName) {
                        locals[keyName] = key;
                    }
                    return getter(scope, locals);
                };
            }

            let lastBlocks = new Map();
            scope.$watchCollection(rhs, collection => {
                let previousNode = anchor;
                const nextBlocks = new Map();
                const order = [];
                let keys;
                let idFn;
                if (isArrayLike(collection)) {
                    keys = collection;
                    idFn = trackBy || ((key, value) => hashKey(value));
                } else {
                    idFn = trackBy || (key => key);
                    keys = [];
                    if (collection && typeof collection === 'object') {
                        Object.keys(collection).forEach(key => {
                            if (key.charAt(0) !== '$') {
                                keys.push(key);
                            }
                        });
                        keys.sort();
                    }
                }
                const length = keys.length;
                for (let index = 0; index < length; index++) {
                    const key = collection === keys ? index : keys[index];
                    const value = collection[key];
                    let id = idFn(key, value, index);
                    if (nextBlocks.has(id)) {
                        // Повтор без track by: AngularJS падает с ошибкой, здесь элемент получает отдельный блок.
                        id = `${id}#${index}`;
                    }
                    if (lastBlocks.has(id)) {
                        const block = lastBlocks.get(id);
                        lastBlocks.delete(id);
                        nextBlocks.set(id, block);
                        order[index] = block;
                    } else {
                        const block = { id };
                        nextBlocks.set(id, block);
                        order[index] = block;
                    }
                }

                lastBlocks.forEach(block => {
                    const nodes = blockNodes(block);
                    nodes.forEach(node => {
                        node.__sdRemoved = true;
                        if (node.nodeType === 1) {
                            runtime.animate.leave(node);
                        } else if (node.parentNode) {
                            node.parentNode.removeChild(node);
                        }
                    });
                    block.scope.$destroy();
                });

                for (let index = 0; index < length; index++) {
                    const key = collection === keys ? index : keys[index];
                    const value = collection[key];
                    const block = order[index];
                    if (index > 0 && order[index - 1] && order[index - 1].end) {
                        previousNode = order[index - 1].end;
                    }
                    let childScope;
                    if (block.scope) {
                        childScope = block.scope;
                        let nextNode = previousNode;
                        do {
                            nextNode = nextNode.nextSibling;
                        } while (nextNode && nextNode.__sdRemoved);
                        if (blockStart(block) !== nextNode) {
                            let after = previousNode;
                            blockNodes(block).forEach(node => {
                                if (node.nodeType === 1) {
                                    runtime.animate.move(node, after.parentNode, after);
                                } else {
                                    after.parentNode.insertBefore(node, after.nextSibling);
                                }
                                after = node;
                            });
                        }
                        previousNode = block.end;
                    } else {
                        childScope = scope.$new();
                    }
                    childScope[valueName] = value;
                    if (keyName) {
                        childScope[keyName] = key;
                    }
                    childScope.$index = index;
                    childScope.$first = index === 0;
                    if (!block.scope) {
                        const end = document.createComment(` end ngRepeat: ${expression} `);
                        const after = previousNode;
                        block.scope = childScope;
                        block.end = end;
                        block.clone = transclude(childScope, clone => {
                            after.parentNode.insertBefore(end, after.nextSibling);
                            after.parentNode.insertBefore(clone, end);
                        });
                        if (block.clone.isConnected) {
                            runtime.animate.enter(block.clone, after.parentNode, after);
                        }
                        previousNode = end;
                    }
                }
                lastBlocks = nextBlocks;
            });
        },
    });
}
